import api, { deduplicatedApi } from "./index";

export const getDashboardData = (data = "today") => {
  //   console.log({ data });
  return api.get(`api/ai-voice-caller/analytics/?filter=${data}`);
};

/**
 * Global search across Properties, Leads, and Contacts
 * @param {Object} params - Search parameters
 * @param {string} params.q - Search query (required)
 * @param {number} params.limit - Max results per entity (default 5)
 * @param {string} params.entity - Specific entity to search: 'properties', 'leads', 'contacts', or 'all' (default)
 */
export const globalSearch = (params = {}) => {
  return deduplicatedApi.get("lead-management/search/", { params });
};

/**
 * Quick search suggestions as user types
 * @param {Object} params - Search parameters
 * @param {string} params.q - Partial search query
 * @param {number} params.limit - Max suggestions (default 8)
 */
export const getSearchSuggestions = (params = {}) => {
  return deduplicatedApi.get("lead-management/search/suggestions/", { params });
};

/**
 * The sale and rental campaigns at one property's address, for the
 * "Sale info / Rental info" switch on the property pages (Monday 13245253503).
 * @param {Object} params
 * @param {string} params.source - 'crm' (/properties/:id) or 'collings' (/collings-properties/:id)
 * @param {number|string} params.id - that page's id
 */
export const getPropertyCampaigns = (params = {}) => {
  return deduplicatedApi.get("lead-management/search/campaigns/", { params });
};

/**
 * Real Estate News for the dashboard widget — live AU property articles, each
 * with its real deep-article link. Served from a Redis-cached backend RSS feed.
 * @returns {Promise} { results: [{ title, description, date, source, link, image }] }
 */
export const getRealEstateNews = () => {
  return api.get("kpi-tracker/dashboard/news/");
};

/**
 * Contract of Sale read receipts — contracts the current agent has emailed to
 * buyers, with Sent / Opened status.
 * @param {Object} params - { status?: 'opened' | 'unopened', limit?: number }
 */
export const getSentContracts = (params = {}) => {
  return api.get("property-management/contract-receipts/sent/", { params });
};

// Market Signal Alerts (dashboard panel) — Homer-backed new listings and
// stalling competitor listings, read from the backend's nightly event cache.
export const getMarketSignals = (params = {}) => {
  return api.get("/api/suburb-intelligence/market-signals/", { params });
};

// The full "Notify list" for one new listing: same-street contacts, then
// owner-type contacts in the suburb.
export const getMarketSignalContacts = (listingId, params = {}) => {
  return api.get("/api/suburb-intelligence/market-signals/contacts/", {
    params: { listing_id: listingId, ...params },
  });
};

// Re-listing Opportunities page (Monday 13202980753): every withdrawn competitor
// listing not back on the market + everything 60+ days on market, filterable.
export const getRelistingOpportunities = (params = {}) => {
  return api.get("/api/suburb-intelligence/market-signals/relisting/", { params });
};

// New Developments page (Monday 12899126094): every open BCI project in the
// patch, searchable by suburb and narrowed by stage.
export const getNewDevelopments = (params = {}) => {
  return api.get("/api/suburb-intelligence/market-signals/developments/", { params });
};

// One project with everything BCI sent, for the detail panel.
export const getNewDevelopment = (id) => {
  return api.get(`/api/suburb-intelligence/market-signals/developments/${encodeURIComponent(id)}/`);
};

// The New Developments sidebar's saved searches (each agent's own).
export const getDevelopmentSavedSearches = () => {
  return api.get("/api/suburb-intelligence/market-signals/developments/saved-searches/");
};

export const saveDevelopmentSearch = (name, params) => {
  return api.post("/api/suburb-intelligence/market-signals/developments/saved-searches/", { name, params });
};

export const deleteDevelopmentSearch = (id) => {
  return api.delete(`/api/suburb-intelligence/market-signals/developments/saved-searches/${id}/`);
};

// "Assign": the property becomes an appraisal in the caller's Off Market
// Pipeline (New column) and leaves the Re-listing list.
// With leadId: records the seller lead made from "Add Lead" (CRM-2026-00114)
// so the property leaves the Re-listing list.
export const assignRelistingOpportunity = (campaignId, leadId) => {
  return api.post("/api/suburb-intelligence/market-signals/relisting/assign/", {
    campaign_id: campaignId,
    ...(leadId ? { lead_id: leadId } : {}),
  });
};

// A note on a re-listing row that every agent sees ("spoken to the owner"), so
// the office does not double up (CRM-2026-00116). Returns the saved note.
// The Re-listing row's Edit form (DOMA 13382334362): replace the owners the
// database team entered, [{name, phone, email}], at most two. Returns the row's
// owner_contacts. Media / Tech and super admins only.
export const saveRelistingOwners = (campaignId, owners) =>
  api.put("/api/suburb-intelligence/market-signals/relisting/owners/", {
    campaign_id: campaignId,
    owners,
  });

export const addRelistingNote = (campaignId, content) => {
  return api.post("/api/suburb-intelligence/market-signals/relisting/notes/", {
    campaign_id: campaignId,
    content,
  });
};
